import React from 'react';
import PropTypes from 'prop-types';
import Relay from 'react-relay';
import {Link} from 'react-router';
import ChannelIcon from '../channel-defs/ChannelIcon.react';
import PagingGrid, {defaultPageSize, processPagingParams} from '../fw/PagingGrid.react';
import AppPanel from '../fw/AppPanel.react';

import types from '../../common/model-api/types';
import urlMapping from '../app/URLMapping';
import {injectIntl, defineMessages, FormattedMessage, FormattedDate, FormattedTime, IntlShape} from 'react-intl';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import ChannelHeader from '../channels/ChannelHeader.react';
import RefreshButton from '../jobs/RefreshButton.react';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import { ERROR_PERMANENT, ERROR_TEMPORARY, ERROR_OK, ERROR_UNKNOWN } from '../../common/model-api/ChannelErrorDefType';
import ConfirmDialog from '../fw/ConfirmDialog.react';
import MarkChannelErrorMutation from './MarkChannelErrorMutation.react';
import LoadingButton from '../fw/LoadingButtonNew.react';
import { User } from '../../common/types/types';

const messages = defineMessages({
    title: {id: 'channelErrors.title', 'defaultMessage': 'List of Errors'},
    displayNameTitle: {id: 'channelErrors.name', 'defaultMessage': 'Display name'},
    searchName: {id: 'channelErrors.searchName', 'defaultMessage': 'Search by code'},
    referencedId: {id: 'channelErrors.referencedId', 'defaultMessage': 'Search by referenced ID'},
    markOkDescription: {id: 'channelErrors.markOkDescription', 'defaultMessage': 'Mark all errors as ok'},
    markOkButton: {id: 'channelErrors.markOkButton', 'defaultMessage': 'Mark ok'},
});

export function renderErrorTitle(item) {
    let prefix = "";
    if (item.hasError) {
        if (item.type === ERROR_PERMANENT || item.type === ERROR_UNKNOWN || !item.type) {
            prefix = "❌";
        } else if (item.type === ERROR_TEMPORARY) {
            prefix = "⚠️";
        } else if (item.type === ERROR_OK) {
            prefix = "✅";
        }
    } else {
        prefix = "✅";
    }

    let title = "";

    if (item.channelErrorDef) {
        title = item.channelErrorDef.title;

        if (item.channelErrorParams) {
            let res = JSON.parse(item.channelErrorParams) || [];

            for (let i = 1; i < 20; i++) {
                let value = "XXX";
                if (res[i]) {
                    value = res[i];
                }

                title = title.replace(new RegExp("\\$" + i, "gm"), value);

            }

        }
    }

    if (prefix) {
        return <span title={item.type}>{prefix} {title}</span>;
    }
}

interface ChannelErrorListProps {
 intl: IntlShape;
 relay?: object;
 user: User;
}

class ChannelErrorList extends React.Component<ChannelErrorListProps> {

    static propTypes = {
        intl: PropTypes.object.isRequired,
        relay: PropTypes.object,
        user: PropTypes.object.isRequired
    };

    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            loadingError: false,
        };
    }

    invokeMarkOk() {
        let { user } = this.props;
        let channel = user.organisation.channel;

        return new Promise((resolve, reject) => {
            let onSuccess = (response) => {
                resolve();
            };

            this.props.relay.commitUpdate(
                new MarkChannelErrorMutation({
                    channel: channel,
                    actionMarkOk: true,
                }), {onSuccess: onSuccess, onFailure: reject}
            );
        });
    }


    onClickMarkOk() {
        return new Promise((resolve, reject) => {
            let confirmDialogConfig  = {
                message: this.props.intl.formatMessage(messages.markOkDescription),
                confirm: this.props.intl.formatMessage(messages.markOkButton),
                confirmStyle: 'btn-danger'
            };

            return ConfirmDialog(this.props.intl, confirmDialogConfig).then(() => {
                return this.invokeMarkOk().then(() => {
                    return resolve();
                });
            }).catch(() => {
                resolve();
            });
        });
    }

    typeName = 'channelErrors';
    listUrlPart = 'list';

    search = (event) => {
        this.setState({
            loading: true,
            loadingError: false,
        });

        let values = {};

        if (this.refs.searchDisplayNameBoxRef.value) {
            values["displayNameLike"] = this.refs.searchDisplayNameBoxRef.value;
        }
        if (this.refs.searchReferenceIdBoxRef.value) {
            values["referencedId"] = this.refs.searchReferenceIdBoxRef.value;
        }

        this.props.relay.setVariables(values, (state) => {
            if (state.done) {
                this.setState({
                    loading: false,
                    loadingError: false,
                });
            } else if (state.error) {
                this.setState({
                    loading: false,
                    loadingError: true,
                });
            }
        });
    };

    reset = () => {
        if (this.refs.searchDisplayNameBoxRef) {
            this.refs.searchDisplayNameBoxRef.value = "";
        }
        if (this.refs.searchReferenceIdBoxRef) {
            this.refs.searchReferenceIdBoxRef.value = "";
        }

        this.props.relay.setVariables({
            displayNameLike: "",
            referenceIdLike: "",
            referenceId: "",
        });
    };

    render() {
        const {user} = this.props;
        let data = user.organisation.channel;
        const list = data.channelErrors;
        let type = types[this.typeName];

        return (
            <AppPanel breadcrumbParams={[
                {type: types['channels'], data: data},
                {type: types['organisations'], data: this.props.user.organisation}]
            } className="fetchList" title={this.props.intl.formatMessage(messages.title)}>
                <ChannelHeader data={user.organisation.channel} organisation={this.props.user.organisation}>
                    <h3><FormattedMessage defaultMessage="Errors" id="channelError.list" /></h3>
                </ChannelHeader>

                <div className="btn-toolbar" role="toolbar">
                    <RefreshButton relay={this.props.relay} />

                    <LoadingButton className="btn btn-default" icon="fa-check" key='check' onClick={() => { this.onClickMarkOk(); }}  text={<FormattedMessage defaultMessage="Mark all as ok" id="channelErrors.marOk" />} />

                    <div aria-label="..." className="btn-group" role="group" style={{paddingRight: '1em'}}>
                        <Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, this.listUrlPart, data)}><FormattedMessage defaultMessage="All" id="channelErrors.listAll" /></Link>
                        <Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, this.listUrlPart + '-ok', data)}><FormattedMessage defaultMessage="Ok" id="channelErrors.listOk" /></Link>
                        <Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, this.listUrlPart + '-fail', data)}><FormattedMessage defaultMessage="Errors" id="channelErrors.listFail" /></Link>
                        <Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, this.listUrlPart + '-forever', data)}><FormattedMessage defaultMessage="Ignored" id="channelErrors.listIgnored" /></Link>
                    </div>

                    <div aria-label="..." className="btn-group" role="group" style={{paddingRight: '1em'}}>
                        <Link activeClassName="active" className="btn btn-default" onlyActiveOnIndex to={urlMapping.getUrl(type, this.listUrlPart, data)}><FormattedMessage defaultMessage="Tabular view" id="channelErrors.tabularView" /></Link>
                        <Link className="btn btn-default" to={urlMapping.getUrl(type, this.listUrlPart + '-type', data)}><FormattedMessage defaultMessage="Grouped by channel error" id="channelErrors.groupedView" /></Link>
                    </div>

                    <div className="input-group" style={{maxWidth: '200px'}}>
                        <input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.searchName)} ref="searchDisplayNameBoxRef" type="text" />
                    </div>

                    <div className="input-group" style={{maxWidth: '200px'}}>
                        <input className="form-control" name="search" placeholder={this.props.intl.formatMessage(messages.referencedId)} ref="searchReferenceIdBoxRef" type="text" />
                    </div>

                    <div aria-label="..." className="btn-group" role="group" style={{paddingRight: '1em'}}>
                        <div className="input-group-btn">
                            <button className="btn btn-outline-secondary" onClick={this.search} type="button">{this.state.loading ? <span className="fa fa-refresh fa-spin"/> : (this.state.loadingError ? <span className="fa fa-exclamation" style={{color: 'red'}}/> : <span className="fa fa-search"/>)}</button>
                        </div>
                        <div className="input-group-btn">
                            <button className="btn btn-outline-secondary" onClick={this.reset} type="button"><span className="fa fa-times"/></button>
                        </div>
                    </div>
                </div>

                <PagingGrid
                    columns={{
                        'channelDef': {
                            header: (<span></span>),
                            render: (column, item) => {
                                if (item.channelDef) {
                                    return <ChannelIcon channelDef={item.channelDef} size={32} />;
                                }

                                return null;
                            }
                        },
                        'sourceDef.name': {},
                        'channelErrorDef.title': {
                            header: (<span></span>),
                            render: (column, item) => {
                                return renderErrorTitle(item);
                            }
                        },
                        'displayName': {
                            render: (column, item) => {
                                let title = "";
                                if (item.displayName) {
                                    title = item.displayName;
                                }
                                if (item['objectDate']) {
                                    return <span>{title}<br/><small><FormattedDate value={item['objectDate']}/></small></span>;
                                } else {
                                    return <span>{title}</span>;
                                }
                            }
                        },
                        'referencedId': {
                            render: (column, item) => {
                                let title = "";
                                if (item.referencedId) {
                                    title = item.referencedId;
                                }
                                return <span>{title}</span>;
                            }
                        },
                        'firstTransferAt': {
                            render: (column, item) => {
                                return <span><FormattedDate value={item[column]}/> <FormattedTime hour='numeric' minute='numeric' second='numeric' value={item[column]}/></span>;
                            }
                        },
                        'lastTransferAt': {
                            render: (column, item) => {
                                return <span><FormattedDate value={item[column]}/> <FormattedTime hour='numeric' minute='numeric' second='numeric' value={item[column]}/></span>;
                            }
                        },
                        'transferCount': {
                            render: (column, item) => {
                                return <span>{item[column] || 0}&nbsp;&times;{item['errorCount'] > 0 ? <small style={{color:"red"}}>&nbsp;(<FormattedMessage defaultMessage="Error:" id="channelErrors.error" /> {item['errorCount'] || 0}&nbsp;&times;)</small> : undefined}</span>;
                            }
                        },
                    }}
                    edges={list}
                    getUrl={(row, column) => {
                        return urlMapping.getUrl('channelErrors', 'view', row);
                    }}
                    metadata={type}
                    pageInfo={list.pageInfo}
                    relay={this.props.relay}
                />
            </AppPanel>
        );
    }
}

export default Relay.createContainer(wrapNotFound(injectIntl(ChannelErrorList), (props) => {
    return props.user.organisation && props.user.organisation.channel;
}), {
    initialVariables: {
        organisationId: '',
        channelId: '',
        first: defaultPageSize,
        last: null,
        after: null,
        before: null,
        type: null,
        success: null,
        resolvableBy: null,
        displayNameLike: null,
        referencedIdLike: null,
        referencedId: null,
        hasError: null,
        ignoredError: null,
        ignoreForFuture: null,
    },
    prepareVariables (variables) {
        processPagingParams(variables);

        if (!variables.organisationId) {
            variables.organisationId = getLastOrganisationId();
        }

        if (variables.success === 'fail') {
            variables.hasError = true;
            variables.ignoredError = null;
            variables.ignoreForFuture = null;
        } else if (variables.success === 'ok') {
            variables.hasError = false;
            variables.ignoredError = null;
            variables.ignoreForFuture = null;
        } else if (variables.success === 'ignored') {
            variables.hasError = null;
            variables.ignoredError = true;
            variables.ignoreForFuture = null;
        } else if (variables.success === 'forever') {
            variables.hasError = null;
            variables.ignoredError = null;
            variables.ignoreForFuture = true;
        } else {
            variables.hasError = null;
            variables.ignoredError = null;
            variables.ignoreForFuture = null;
        }

        if (variables.displayNameLike) {
            variables.hasError = null;
            variables.ignoredError = null;
            variables.ignoreForFuture = null;
        }
        if (variables.referencedIdLike) {
            variables.hasError = null;
            variables.ignoredError = null;
            variables.ignoreForFuture = null;
        }
        if (variables.referencedId) {
            variables.hasError = null;
            variables.ignoredError = null;
            variables.ignoreForFuture = null;
        }


        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                    channel(id: $channelId) {
                        id,
                        name,
                        organisation_id,
                        channelDef {
                            id,
                            name,
                            icon
                        },
                        channelErrors(first: $first, after: $after, before: $before, last: $last, type: $type, hasError: $hasError, displayNameLike: $displayNameLike, referencedId: $referencedId, referencedIdLike: $referencedIdLike, resolvableBy: $resolvableBy, ignoredError: $ignoredError, ignoreForFuture: $ignoreForFuture) {

                            edges {
                                node {
                                    createdAt,
                                    type,
                                    hasError,
                                    id,
                                    realId,
                                    channel_id,
                                    channel {
                                        id,
                                        name,
                                        channelDef {
                                            id,
                                            name,
                                            icon,
                                        },
                                    },
                                    channelDef {
                                        id,
                                        name,
                                        icon,
                                    },
                                    sourceDef {
                                        id,
                                        name,
                                    },
                                    channelErrorParams,
                                    channelErrorMessage,
                                    channelErrorDef {
                                        id,
                                        title,
                                        name,
                                        type,
                                    },
                                    firstTransferAt,
                                    lastTransferAt,
                                    entityDefKey,
                                    displayName,
                                    referencedId,
                                    lastError,
                                    transferCount,
                                    errorCount,
                                    objectDate,
                                    organisation {
                                        id,
                                        name,
                                    }
                                    organisation_id,
                                    flowDef {
                                        name,
                                        id
                                    },
                                    flow {
                                        name,
                                        id
                                    },
                                }
                            },
                            pageInfo {
                                hasNextPage,
                                hasPreviousPage,
                                startCursor,
                                endCursor
                            }
                        }
                    }
                }
            }
        `,
    },
});
